import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import TaskForm from './components/task/TaskForm';
import TaskStatus from './components/task/TaskStatus';
import Profile from './components/profile/Profile';
import AdminPanel from './components/admin/AdminPanel';
import Messages from './components/messages/Messages';
import TrainingPage from './components/training/TrainingPage';
import { authAPI, usersAPI } from './services/apiService';
import { initSocket, connectSocket, listenForUpdates, removeListeners, disconnectSocket, updateSocketAuth } from './services/socketService';
import 'bootstrap/dist/css/bootstrap.min.css';
import Login from './components/auth/Login';
import Footer from './components/common/Footer';
import Header from './components/common/Header';
import EasterEggOverlay, { effectsList } from './components/common/EasterEggOverlay';
import ErrorBoundary from './components/common/ErrorBoundary';
import HelpWidget from './components/common/HelpWidget';
import { generationsLabel } from './utils/pluralize';

function App() {
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('home'); // 'home' | 'profile' | 'admin'
  const [openProfileTab, setOpenProfileTab] = useState(null); // 'payment' | null — открыть вкладку при переходе в профиль
  const [preselectedTariffType, setPreselectedTariffType] = useState(null); // type из /confirm (1, 2, 3...)
  const [preselectedTariffId, setPreselectedTariffId] = useState(null); // uuid из /confirm?tariff=
  const [confirmParams, setConfirmParams] = useState(null); // { type, tariff, phone, login, email }
  const [eggEffect, setEggEffect] = useState(null);
  const remainingRequests =
    user?.requestsBalance ??
    user?.requests_balance ??
    user?.balance ??
    user?.requests ??
    null;

  // Цвет баланса: зелёный > 50, жёлтый 10–50, красный < 10
  const balanceBadgeVariant =
    remainingRequests === null
      ? 'secondary'
      : remainingRequests >= 50
        ? 'success'
        : remainingRequests >= 10
          ? 'warning'
          : 'danger';

  // Проверка токена при загрузке приложения
  useEffect(() => {
    const validateToken = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const userData = localStorage.getItem('user');
      
      if (accessToken && userData) {
        try {
          const response = await authAPI.validateToken();
          if (response.valid) {
            setAuthenticated(true);
            setUser(JSON.parse(userData));
            // Обновляем авторизацию WebSocket
            updateSocketAuth(accessToken);
          } else {
            // Токен невалиден, очищаем хранилище
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
          }
        } catch (err) {
          // Ошибка при проверке токена
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    validateToken();
  }, []);

  // Обработка /confirm — переход с лендинга с параметрами type, tariff, phone, login, email
  useEffect(() => {
    if (window.location.pathname === '/confirm') {
      const params = new URLSearchParams(window.location.search);
      const type = params.get('type');
      const tariff = params.get('tariff');
      const phone = params.get('phone');
      const login = params.get('login');
      const email = params.get('email');
      if (type || tariff || phone || login || email) {
        const stored = {
          type: type || null,
          tariff: tariff || null,
          phone: phone || null,
          login: login || null,
          email: email || null,
        };
        sessionStorage.setItem('confirmParams', JSON.stringify(stored));
        setConfirmParams(stored);
      }
      // Очищаем URL от query
      window.history.replaceState({}, '', '/confirm');
    } else {
      // При обычном заходе читаем confirmParams из sessionStorage (если есть)
      const stored = sessionStorage.getItem('confirmParams');
      if (stored) {
        try {
          setConfirmParams(JSON.parse(stored));
        } catch (e) {
          sessionStorage.removeItem('confirmParams');
        }
      }
    }
  }, []);

  // Если пользователь уже авторизован и зашёл на /confirm — переходим на оплату
  useEffect(() => {
    if (!loading && authenticated) {
      const stored = sessionStorage.getItem('confirmParams');
      if (stored) {
        try {
          const { type, tariff } = JSON.parse(stored);
          if (type || tariff) {
            setCurrentPage('profile');
            setOpenProfileTab('payment');
            if (tariff) {
              setPreselectedTariffId(tariff);
              setPreselectedTariffType(null);
            } else {
              setPreselectedTariffType(parseInt(type, 10));
              setPreselectedTariffId(null);
            }
            sessionStorage.removeItem('confirmParams');
            setConfirmParams(null);
          }
        } catch (e) {
          sessionStorage.removeItem('confirmParams');
        }
      }
    }
  }, [loading, authenticated]);

  // Инициализация WebSocket для аутентифицированных пользователей
  useEffect(() => {
    if (authenticated) {
      initSocket();
      connectSocket();
      listenForUpdates(dispatch);
      
      return () => {
        removeListeners();
        disconnectSocket();
      };
    }
  }, [authenticated, dispatch]);

  const handleLogin = (accessToken, userData) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setAuthenticated(true);
    setUser(userData);

    // Если пришли с /confirm — переходим на профиль, вкладка оплаты, предвыбран тариф
    const stored = sessionStorage.getItem('confirmParams');
    if (stored) {
      try {
        const { type, tariff } = JSON.parse(stored);
        if (type || tariff) {
          setCurrentPage('profile');
          setOpenProfileTab('payment');
          if (tariff) {
            setPreselectedTariffId(tariff);
            setPreselectedTariffType(null);
          } else {
            setPreselectedTariffType(parseInt(type, 10));
            setPreselectedTariffId(null);
          }
        }
        sessionStorage.removeItem('confirmParams');
      } catch (e) {
        sessionStorage.removeItem('confirmParams');
      }
      setConfirmParams(null);
    }

    // Обновляем авторизацию WebSocket
    updateSocketAuth(accessToken);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUserBalance = async () => {
    try {
      const data = await usersAPI.getProfile();
      const profile = data?.user ?? data;
      if (profile) {
        setUser(profile);
        localStorage.setItem('user', JSON.stringify(profile));
      }
    } catch (err) {
      console.error('Failed to refresh balance:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      disconnectSocket();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setAuthenticated(false);
      setUser(null);
      setCurrentPage('home');
    }
  };

  // Проверяем редирект после оплаты и переключаемся на профиль
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentComplete = urlParams.get('paymentComplete');
    
    if (paymentComplete === 'true' && authenticated) {
      setCurrentPage('profile');
    }
  }, [authenticated]);

  // Пасхалка F1 / ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        const random = effectsList[Math.floor(Math.random() * effectsList.length)];
        setEggEffect(random);
      }
      if (e.key === 'Escape') {
        setEggEffect(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return (
      <>
        <main className="main-content">
          <Container className="py-5">
            <Row className="justify-content-center">
              <Col md={8} lg={6}>
                <Card className="shadow-sm">
                  <Card.Body className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p>Проверка авторизации...</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  if (!authenticated) {
    return (
      <ErrorBoundary>
        <div className="login-page">
          <div className="login-background">
            <div className="login-container">
              <Login onLogin={handleLogin} confirmParams={confirmParams} onConfirmParamsConsumed={() => setConfirmParams(null)} />
            </div>
          </div>
          <Footer />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app-shell">
        <Header
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          user={user}
          onLogout={handleLogout}
        />

        <main className={`main-content ${currentPage === 'home' ? 'main-content--gradient' : ''}`}>
            {currentPage === 'home' ? (
              <div className="create-protocol-page">
                <Container className="py-3">
                  <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                      <Card className="create-protocol-widget border-0">
                        <Card.Body>
                          <div className="create-protocol-header">
                            <h1 className="create-protocol-title">Создать протокол</h1>
                            <div className="create-protocol-actions">
                              {remainingRequests !== null && (
                                <Badge bg={balanceBadgeVariant} className="glass-badge balance-badge">
                                  Осталось: {generationsLabel(remainingRequests)}
                                </Badge>
                              )}
                              <button
                                type="button"
                                className="glass-btn-secondary"
                                onClick={() => {
                                  setCurrentPage('profile');
                                  setOpenProfileTab('payment');
                                }}
                              >
                                Пополнить
                              </button>
                            </div>
                          </div>
                          {remainingRequests === 0 && (
                            <Alert variant="warning" className="py-2 glass-alert">
                              У вас нет доступных генераций. Пополните баланс, чтобы продолжить.
                            </Alert>
                          )}
                          <TaskForm onGenerationSuccess={refreshUserBalance} />
                          <TaskStatus />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              </div>
        ) : currentPage === 'profile' ? (
          <Profile
            user={user}
            onUpdate={handleUserUpdate}
            initialTab={openProfileTab}
            onInitialTabHandled={() => setOpenProfileTab(null)}
            preselectedTariffType={preselectedTariffType}
            preselectedTariffId={preselectedTariffId}
            onPreselectedTariffConsumed={() => {
              setPreselectedTariffType(null);
              setPreselectedTariffId(null);
            }}
          />
        ) : currentPage === 'messages' ? (
          <Container className="py-3">
            <Messages user={user} />
          </Container>
        ) : currentPage === 'training' ? (
          <TrainingPage />
        ) : currentPage === 'admin' ? (
          <AdminPanel user={user} />
        ) : null}
        </main>
        <Footer />
      </div>
      <HelpWidget />
      {eggEffect && (
        <EasterEggOverlay effect={eggEffect} onClose={() => setEggEffect(null)} />
      )}
    </ErrorBoundary>
  );
}

export default App;

